import React, { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { AuthHeroBackground } from './AuthHeroBackground'
import { BrandMark } from '@/components/brand-mark'
import { useLocationStore } from '@stores/location'
import { useSignInSignUpStore } from '@stores/auth'
import { useClientStore } from '@stores/client'
import 'react-datepicker/dist/react-datepicker.css'
import PasswordRecovery from './PasswordRecovery'
import { useUserStore } from '@/client/stores/user'

const LoadingSpinner: React.FC = () => (
  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
);

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner />
  </div>
);


/** A password that verified, waiting on the account's two-step verification code. */
type MfaChallenge = { token: string; method: 'sms' | 'email'; destination: string }

/** ff-api answers an enrolled admin's password with 401 `mfa_required`. */
function mfaChallengeOf(body: unknown): MfaChallenge | null {
  const wrapped = body as { data?: Record<string, string> } & Record<string, string>
  const d = wrapped?.data ?? wrapped
  if (d?.error !== 'mfa_required' || !d.mfa_token) return null
  return { token: d.mfa_token, method: d.method === 'sms' ? 'sms' : 'email', destination: d.destination ?? '' }
}

function errorText(error: any, fallback: string): string {
  return error?.data?.data?.error_description || error?.data?.error_description || error?.message || fallback
}

const SignInSignUpForm: React.FC<{ onOpenChange: (open: any) => void }> = ({ onOpenChange }) => {
  const formRef = React.useRef<HTMLFormElement>(null)
  const { fetchLocation } = useLocationStore()
  const { request } = useClientStore()
  const { refreshUser } = useUserStore()
  const {
    toggleModal,
    usePhone, emailOrPhone, password, showPassword,
    setUsePhone, setEmailOrPhone, setPassword, setShowPassword,
    resetForms
  } = useSignInSignUpStore()

  React.useEffect(() => { (async () => await fetchLocation())() }, [])

  const [loading, setLoading] = React.useState(false)
  const [loginError, setLoginError] = React.useState("")
  const [registerError, setRegisterError] = React.useState("")
  const [mfa, setMfa] = React.useState<MfaChallenge | null>(null)
  const [mfaCode, setMfaCode] = React.useState("")
  const [resending, setResending] = React.useState(false)

  function cancelMfa() {
    setMfa(null)
    setMfaCode("")
    setPassword("")
  }

  async function handleMfaVerify(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!mfa || !/^\d{6}$/.test(mfaCode)) return
    setLoginError("")
    try {
      setLoading(true)
      const response = await request<APIResponse<string>>(
        '/api/admin/auth/user/account/login/mfa/verify', 'POST',
        { mfa_token: mfa.token, code: mfaCode }, { timeout: 30_000 })
      if (response.status !== 200) throw new Error('Verification failed')
      setMfa(null)
      setMfaCode("")
      refreshUser(() => {})
    }
    catch (error: any) {
      const code = error?.data?.data?.error || error?.data?.error
      // A spent challenge cannot be retried; the password has to be entered again.
      if (code === 'mfa_expired') cancelMfa()
      setLoginError(errorText(error, 'Verification failed'))
      setTimeout(() => setLoginError(""), 10000)
    }
    finally {
      setLoading(false)
    }
  }

  async function handleMfaResend() {
    if (!mfa) return
    setLoginError("")
    try {
      setResending(true)
      await request('/api/admin/auth/user/account/login/mfa/resend', 'POST', { mfa_token: mfa.token })
    }
    catch (error: any) {
      const code = error?.data?.data?.error || error?.data?.error
      if (code === 'mfa_expired') cancelMfa()
      setLoginError(errorText(error, 'Could not send a new code'))
    }
    finally {
      setResending(false)
    }
  }

  function handleClose() {
    toggleModal()
    resetForms()
    setLoginError("")
    setRegisterError("")
  }

  async function handleSignIn(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoginError("")

    if (usePhone && !emailOrPhone) {
      setLoginError("Phone Number is required")
      return
    }
    else if (!emailOrPhone) {
      setLoginError("Email address or username is required")
      return
    }

    if (!password) {
      setLoginError("Password is required")
      return
    }

    try {
      setLoading(true)
      const payload = {
        username: usePhone ? undefined : emailOrPhone,
        phone: usePhone ? emailOrPhone : undefined,
        password
      }

      // const response = await request<APIResponse<string>>('/auth/v2/user/account/login', 'POST', payload)
      const response = await request<APIResponse<string>>('/api/admin/auth/user/account/login', 'POST', payload, { timeout: 30_000 })
      if (response.status !== 200)
        throw new Error('Invalid login credentials')

      refreshUser(() => {})
    }
    catch (error: any) {
      const challenge = mfaChallengeOf(error?.data)
      if (challenge) {
        setMfa(challenge)
        setMfaCode("")
        return
      }
      setLoginError(error.message || 'Login failed')
      setTimeout(() => setLoginError(""), 10000)
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {(loginError || registerError) && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {loginError || registerError}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <a href="/" aria-label="FeelyFeely Admin" className="mb-6 flex justify-center">
            <BrandMark />
          </a>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            Login to your account
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Enter your {usePhone ? 'phone number' : 'email'} below to login to your account
          </p>
        </div>

        {mfa ? (
          <form onSubmit={handleMfaVerify} className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Two-step verification</p>
              <p className="mt-1 text-sm text-gray-500">
                We sent a code by {mfa.method === 'sms' ? 'text message' : 'email'} to{' '}
                <span className="font-medium text-gray-900">{mfa.destination}</span>.
              </p>
            </div>
            <div className="space-y-1">
              <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                6-digit code
              </label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-lg tracking-[0.4em] text-gray-900 shadow-sm focus:border-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-900"
                placeholder="······"
              />
            </div>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleMfaResend}
                disabled={resending || loading}
                className="font-medium text-blue-900 hover:underline disabled:opacity-50"
              >
                {resending ? 'Sending…' : 'Resend code'}
              </button>
              <button
                type="button"
                onClick={cancelMfa}
                disabled={loading}
                className="text-gray-500 hover:text-gray-800 hover:underline disabled:opacity-50"
              >
                Back
              </button>
            </div>
            <button
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              className={`w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-1 ${
                loading || mfaCode.length !== 6 ? 'cursor-not-allowed bg-blue-300' : 'bg-blue-900 hover:bg-blue-800'
              }`}
            >
              {loading ? 'Verifying…' : 'Verify & log in'}
            </button>
          </form>
        ) : (
        <form ref={formRef} onSubmit={handleSignIn} className="space-y-4">
          <div className="flex items-center justify-between">
            <label htmlFor="emailOrPhone" className="block text-sm font-medium text-gray-700">
              {usePhone ? 'Phone Number' : 'Email'}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="usePhone"
                checked={usePhone}
                onChange={(e) => setUsePhone(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-800"
              />
              <label htmlFor="usePhone" className="text-xs text-gray-600">
                Use phone number
              </label>
            </div>
          </div>
          <input
            type={usePhone ? 'tel' : 'email'}
            id="emailOrPhone"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-900"
            placeholder={usePhone ? 'Enter your phone number' : 'm@example.com'}
            value={emailOrPhone}
            onChange={(e) => setEmailOrPhone(e.target.value)}
          />

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <button
                type="button"
                className="text-xs font-medium text-blue-900 hover:underline"
                onClick={() => onOpenChange('recovery')}
              >
                Forgot your password?
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm text-gray-900 shadow-sm focus:border-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-900"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`mt-2 w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-1 ${
              loading || !emailOrPhone.trim() || !password.trim()
                ? 'cursor-not-allowed bg-blue-300'
                : 'bg-blue-900 hover:bg-blue-800'
            }`}
            disabled={loading || !emailOrPhone.trim() || !password.trim()}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        )}

      </div>
    </div>
  )
}

const SignInSignUpPage = () => {
  const [isModalOpen, setIsModalOpen] = React.useState<'signin' | 'recovery'>('signin')
  const { isLoadingUser, user, initialized, initialize, refreshUser } = useUserStore()
  const { initialized: initializedClient, initialize: initializeClient } = useClientStore()
 
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await initializeClient(async () => {
          await initialize()
        });
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };
    
    initializeApp();
  }, [initializeClient, refreshUser]);

   // Wait for both client and user initialization to complete
   if (isLoadingUser || !initialized || !initializedClient) {
    return <LoadingFallback  />;
  }

  // If authenticated after initialization: redirect to admin
  if (user) return <Navigate to="/admin" />

  return (
    <div className="grid min-h-screen bg-black lg:grid-cols-6">
      <div className="relative hidden col-span-4 lg:block bg-black">
        <AuthHeroBackground />
        <div className="absolute bottom-0 z-30 p-10">
          <BrandMark size="lg" inverted />
          <p className="mt-3 max-w-sm text-sm text-gray-200">
            Protected access. Authorized FeelyFeely administrators only. Unauthorized use is prohibited.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 bg-white p-6 md:p-10 col-span-6 lg:col-span-2">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            {isModalOpen === 'signin' ? (
              <SignInSignUpForm onOpenChange={setIsModalOpen} />
            ) : (
              <PasswordRecovery onOpenChange={setIsModalOpen} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignInSignUpPage
